import Spinners from "components/Common/Spinner"
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Col, Form, FormFeedback, Input, Label, Row } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import toast from "react-hot-toast"
import { useUpdateBusinessSignatoryDetailsProfileMutation } from "store/api/profile/profile.api"
import { designationMap } from "helpers/common"
import FullPageLoader from "components/fullPageLoader"
import {
  useGenerateTemplateTokenMutation,
  useLazyGetStatusOfOldDigioRequestQuery,
  useUpdateDiogioRequestStatusMutation,
} from "store/api/kyc/kyc"
import logo from "../../../assets/images/logo-light1.svg"

const defaultValues = {
  designation: "",
  dpin: "",
}

const AuthorisedSignatory = ({ kycData }) => {
  const { user, userType } = useSelector((state) => state.user)
  const [companyType, setCompanyType] = useState("")
  const [designations, setDesignations] = useState([])
  const dispatch = useDispatch()
  const [updateBusinessSignatory, { isLoading: isUpdating }] =
    useUpdateBusinessSignatoryDetailsProfileMutation()

  const [isLoading, setIsLoading] = useState(false)
  const [requestData, setRequestData] = useState(null)
  const [loader, setLoader] = useState(false)
  const [getToken, { isLoading: isFetching }] =
    useGenerateTemplateTokenMutation()
  const [getRequestStatus] = useLazyGetStatusOfOldDigioRequestQuery()
  const [updateDiogioRequestTrigger] = useUpdateDiogioRequestStatusMutation()
  const intervalRef = useRef(null)
  const template_name = "DIGILOCKER_AADHAAR_PAN_ICD"

  useEffect(() => {
    if (user) {
      const constitutionOfBusiness =
        kycData?.businessAdditionalDetails?.constitution_of_business ||
        "Private Limited Company"
      setCompanyType(constitutionOfBusiness)
    }
  }, [user, kycData])

  useEffect(() => {
    validation.setValues({
      designation: kycData?.businessAdditionalDetails.designation,
      dpin: kycData?.businessAdditionalDetails.dpin,
    })
  }, [kycData])

  useEffect(() => {
    const items = designationMap[companyType] || []
    setDesignations(items)
  }, [companyType])

  const validation = useFormik({
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),
    validateOnMount: false,
    onSubmit: async (values) => {
      onFormUpdate(values)
    },
  })

  const onFormUpdate = async (values) => {
    try {
      const { success } = await updateBusinessSignatory({
        ...values,
      }).unwrap()

      if (success) {
        getDigioToken()
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    onGetRequestStatus()

    return () => {
      if (intervalRef) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const onGetRequestStatus = async () => {
    try {
      const { data } = await getRequestStatus({ template_name }).unwrap()
      setRequestData(data)
      if (data.status === "kyc.request.approved" && intervalRef?.current) {
        window.location.reload()
        clearInterval(intervalRef.current)
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (requestData) {
      if (requestData.status === "kyc.request.completed") {
        setLoader(true)
        const id = setInterval(() => {
          onGetRequestStatus()
        }, 5000)
        intervalRef.current = id
      }
    }
  }, [requestData])

  const getDigioToken = async () => {
    try {
      setIsLoading(true)
      const { data, success, message } = await getToken({
        template_name,
      }).unwrap()

      if (success) {
        if (window.Digio) {
          const options = {
            environment: "production", //<sandbox, production>
            callback: function (response) {
              if (response.hasOwnProperty("error_code")) {
                console.log("Error occurred in process", response)
              } else {
                updateDiogioRequestTrigger({
                  id: response.digio_doc_id,
                }).unwrap()
                setLoader(true)
                setTimeout(() => {
                  setLoader(false)
                  window.location.reload()
                }, 10000)
              }
            },
            event_listener: function (event) {
              console.log("event_listener", event)
            },
            is_iframe: true,
            logo: logo,
            theme: {
              primaryColor: "#000000",
              secondaryColor: "#000000",
            },
          }
          if (data?.documentId) {
            const digio = new window.Digio(options)
            digio.init()

            const documentId = data.documentId
            const identifier = data.identifier
            const tokenId = data.tokenId

            digio.submit(documentId, identifier, tokenId)
          }
          // If using token approach:
        } else {
          console.error("Digio SDK not loaded")
        }
      } else {
        toast.error(message, { id: "toastMessage" })
      }
    } catch (error) {
      toast.error("Something went wrong, Please try again after sometime", {
        id: "toastMessage",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (loader) return <FullPageLoader />
  return (
    <>
      <Row>
        <div className="bank-details-wrapper">
          <h5 className="title">Authorised Person KYC</h5>
          <p>Select the designation of the authorised person to proceed.</p>
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            <Col xl={12}>
              <Label className="col-form-label">
                Designation <span className="error">*</span>
              </Label>
              <select
                name="designation"
                className={`form-select pageSize ${
                  validation.touched.designation &&
                  validation.errors.designation &&
                  "is-invalid"
                }`}
                value={validation.values.designation}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              >
                <option value=""> Select </option>
                {designations.map((item, index) => (
                  <option key={`designations_${index}`} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              {validation.touched.designation &&
              validation.errors.designation ? (
                <FormFeedback type="invalid">
                  {validation.errors.designation}
                </FormFeedback>
              ) : null}
            </Col>
            {["Designated Partner", "Director"].includes(
              validation.values.designation
            ) && (
              <Col xl={12}>
                <Label className="col-form-label">
                  {kycData?.businessAdditionalDetails
                    ?.constitution_of_business ===
                  "Limited Liability Partnership"
                    ? "DPIN"
                    : "DIN"}
                </Label>
                <Input
                  name="dpin"
                  type="text"
                  className="form-control"
                  placeholder="Enter DPIN"
                  value={validation.values.dpin}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.dpin && validation.errors.dpin
                      ? true
                      : false
                  }
                />
                {validation.touched.dpin && validation.errors.dpin ? (
                  <FormFeedback type="invalid">
                    {validation.errors.dpin}
                  </FormFeedback>
                ) : null}
              </Col>
            )}
            <Col sm={12}>
              <div className="d-flex align-items-center mt-4">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={isUpdating || isLoading || isFetching}
                >
                  {isUpdating || isLoading || isFetching ? (
                    <Spinners />
                  ) : (
                    "Save & Next"
                  )}
                </button>
              </div>
            </Col>
          </Form>
        </div>
      </Row>
    </>
  )
}

export default AuthorisedSignatory
